import { formatDate as formatDisplayDate, formatWait } from '@/lib/formatDate';
import React, { useState, useEffect } from 'react';
import {
  IndianRupee, Calendar, Pencil, Printer,
  FileText, Receipt, Loader2, ArrowRight, Star, X, ArrowLeftRight,
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { StatusChip } from '@/components/ui/status-chip';
import { PatientBanner } from '@/components/Patient/PatientBanner';
import { usePermission } from '@/hooks/usePermission';
import {
  getPatient, getPatientInvoices, getPatientPrescriptions,
  getSixMonthsAgoDate, isReportReturn, type QueueEntry,
} from '@/lib/api';
import TodayVitalsCard from '@/components/Vitals/TodayVitalsCard';
import { patientAgeMonths } from '@/lib/vitals';

interface Props {
  patientId: string;
  patientName: string;
  queueEntry: (QueueEntry & { doctorName: string; doctorId?: string }) | null;
  onNewInvoice: (patient: any) => void;
  onCollectPayment: (invoice: any) => void;
  onBookAppointment: (patient: any) => void;
  onEditPatient: (patient: any) => void;
  onPrintPrescription: (rx: any) => void;
  onPrintInvoice: (inv: any) => void;
  onPriority: (entry: QueueEntry) => void;
  onRemoveFromQueue: (entry: QueueEntry) => void;
  /** The clinic's other doctors a waiting patient can move to */
  moveTargets?: { id: string; name: string }[];
  onMove?: (entry: QueueEntry, doctorId: string) => void;
  /** Seen today and back with a test report: same visit, next in the doctor's queue */
  onReturnWithReport: (entry: QueueEntry) => void;
  onViewFullHistory: (patientId: string) => void;
  /** Refresh the queue after vitals are recorded (for its "Vitals done" tag) */
  onVitalsRecorded?: () => void;
}

function formatDate(dateStr: string) {
  return formatDisplayDate(dateStr);
}

function RemoveConfirm({ name, onConfirm, onCancel }: { name: string; onConfirm: () => void; onCancel: () => void }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-destructive font-medium">Remove {name}?</span>
      <button
        onClick={onConfirm}
        className="h-7 px-2.5 rounded text-xs font-medium bg-destructive text-destructive-foreground hover:bg-destructive/90"
      >
        Remove
      </button>
      <button
        onClick={onCancel}
        className="h-7 px-2.5 rounded text-xs font-medium border border-border text-foreground hover:bg-muted"
      >
        Keep
      </button>
    </div>
  );
}

export default function PatientContextPanel({
  patientId,
  patientName,
  queueEntry,
  onNewInvoice,
  onCollectPayment,
  onBookAppointment,
  onEditPatient,
  onPrintPrescription,
  onPrintInvoice,
  onPriority,
  onRemoveFromQueue,
  onReturnWithReport,
  moveTargets = [],
  onMove,
  onViewFullHistory,
  onVitalsRecorded,
}: Props) {
  const { can } = usePermission();
  const [patient, setPatient] = useState<any>(null);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [prescriptions, setPrescriptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmRemove, setConfirmRemove] = useState(false);

  useEffect(() => {
    if (!patientId) return;
    setLoading(true);
    setPatient(null);
    setInvoices([]);
    setPrescriptions([]);

    const from = getSixMonthsAgoDate();
    Promise.all([
      getPatient(patientId),
      can('billing:read') ? getPatientInvoices(patientId, { from }) : Promise.resolve([]),
      can('prescriptions:read') ? getPatientPrescriptions(patientId, { from }) : Promise.resolve([]),
    ]).then(([p, inv, rx]) => {
      setPatient(p);
      setInvoices(Array.isArray(inv) ? inv : []);
      setPrescriptions(Array.isArray(rx) ? rx : []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [patientId]);

  const visitRx = queueEntry?.visitId ? prescriptions.find(rx => rx.visit_id === queueEntry.visitId) : undefined;

  const outstanding = invoices
    .filter(inv => inv.payment_status === 'unpaid' || inv.payment_status === 'partial')
    .reduce((sum, inv) => sum + (Number(inv.total_amount) - Number(inv.paid_amount || 0)), 0);

  // Merge invoices + prescriptions into a unified visit timeline, last 5 entries
  const recentItems = React.useMemo(() => {
    const items: { date: string; type: 'invoice' | 'rx'; data: any }[] = [
      ...invoices.map(inv => ({ date: (inv.invoice_date || inv.created_at || '').split('T')[0], type: 'invoice' as const, data: inv })),
      ...prescriptions.map(rx => ({ date: (rx.date || rx.created_at || '').split('T')[0], type: 'rx' as const, data: rx })),
    ];
    return items.sort((a, b) => b.date.localeCompare(a.date)).slice(0, 6);
  }, [invoices, prescriptions]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const p = patient ?? { name: patientName };
  const displayName = p.name ?? patientName;

  return (
    <div className="p-4 space-y-3">
      {/* Patient — same banner as every patient screen, so allergies are in view at the desk too */}
      <div className="rounded-lg border border-border bg-card">
        <PatientBanner
          patient={{ ...p, name: displayName }}
          size="compact"
          outstanding={outstanding}
          meta={p.last_visit_date ? <span>Last visit {formatDate(p.last_visit_date)}</span> : undefined}
          className="sm:px-4 py-3"
        />
        <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 border-t border-border">
          {can('billing:write') && (
            <Button size="sm" className="gap-1.5" onClick={() => onNewInvoice(p)}>
              <IndianRupee className="w-3.5 h-3.5" /> New invoice
            </Button>
          )}
          {can('appointments:write') && (
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => onBookAppointment(p)}>
              <Calendar className="w-3.5 h-3.5" /> Book appointment
            </Button>
          )}
          {can('patients:write') && (
            <Button size="sm" variant="ghost" className="gap-1.5" onClick={() => onEditPatient(p)}>
              <Pencil className="w-3.5 h-3.5" /> Edit
            </Button>
          )}
          <Button size="sm" variant="ghost" className="gap-1 text-primary ml-auto" onClick={() => onViewFullHistory(patientId)}>
            Full history <ArrowRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Queue status */}
      {queueEntry && (
        <div className="rounded-lg border border-border bg-card px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <span className={`w-10 h-9 rounded flex items-center justify-center font-mono text-base font-semibold tabular-nums flex-shrink-0 ${
                queueEntry.status === 'in_consultation' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'
              }`} aria-label={`Token ${queueEntry.tokenNumber}`}>
                {queueEntry.tokenNumber}
              </span>
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-foreground">
                  {queueEntry.status === 'in_consultation' && <StatusChip tone="accent">In consultation</StatusChip>}
                  {queueEntry.status === 'on_hold' && <StatusChip tone="warning">On hold · test</StatusChip>}
                  {queueEntry.status === 'waiting' && (
                    <>
                      {isReportReturn(queueEntry)
                        ? <StatusChip tone="accent">Back with report</StatusChip>
                        : <StatusChip tone="neutral">Waiting</StatusChip>}
                      {queueEntry.waitingTimeMinutes ? <span className="text-xs text-muted-foreground tabular-nums">{formatWait(queueEntry.waitingTimeMinutes)}</span> : null}
                    </>
                  )}
                  {queueEntry.status === 'completed' && <StatusChip tone="success">Seen</StatusChip>}
                </p>
                {queueEntry.doctorName && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {queueEntry.doctorName.startsWith('Dr.') ? queueEntry.doctorName : `Dr. ${queueEntry.doctorName}`}
                    {queueEntry.status === 'on_hold' && ' · sent for a test, returning to the doctor'}
                  </p>
                )}
              </div>
            </div>
            {queueEntry.status === 'completed' && (
              <div className="flex gap-1.5 items-center flex-shrink-0">
                {/* The doctor writes, the desk prints: this visit's prescription */}
                {visitRx && (
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={() => onPrintPrescription(visitRx)}>
                    <Printer className="w-3.5 h-3.5" /> Print Rx
                  </Button>
                )}
                {/* Seen today and back with the test report: next in the doctor's queue, same visit, no new fee */}
                {can('queue:write') && (
                  <Button size="sm" variant="outline" onClick={() => onReturnWithReport(queueEntry)}>
                    Back with report
                  </Button>
                )}
              </div>
            )}
            {/* Queue actions — only for waiting */}
            {queueEntry.status === 'waiting' && can('queue:write') && (
              <div className="flex gap-1.5 items-center flex-shrink-0">
                {confirmRemove ? (
                  <RemoveConfirm
                    name={queueEntry.patientName}
                    onConfirm={() => { setConfirmRemove(false); onRemoveFromQueue(queueEntry); }}
                    onCancel={() => setConfirmRemove(false)}
                  />
                ) : (
                  <>
                    <Button size="sm" variant="outline" className="gap-1.5" onClick={() => onPriority(queueEntry)}>
                      <Star className="w-3.5 h-3.5" /> Priority
                    </Button>
                    {/* Their doctor had to leave, or they asked for another: next token with that doctor */}
                    {onMove && moveTargets.length > 0 && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="outline" className="gap-1.5">
                            <ArrowLeftRight className="w-3.5 h-3.5" /> Move
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {moveTargets.map(d => (
                            <DropdownMenuItem key={d.id} onClick={() => onMove(queueEntry, d.id)}>
                              {d.name.startsWith('Dr.') ? d.name : `Dr. ${d.name}`}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                    <Button
                      size="sm" variant="ghost"
                      className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => setConfirmRemove(true)}
                      title="Remove from queue" aria-label="Remove from queue"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Today's vitals — recorded here before the doctor sees the patient */}
      <TodayVitalsCard
        patientId={patientId}
        patientName={displayName}
        doctorId={queueEntry && queueEntry.status !== 'completed' && queueEntry.status !== 'cancelled' ? queueEntry.doctorId ?? null : null}
        visitId={queueEntry && queueEntry.status !== 'completed' && queueEntry.status !== 'cancelled' ? queueEntry.visitId ?? null : null}
        canRecord={can('queue:write')}
        ageMonths={patientAgeMonths(p)}
        onRecorded={onVitalsRecorded}
      />

      {/* Recent visits */}
      {recentItems.length > 0 && (
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <div className="flex items-center h-10 px-4 border-b border-border">
            <h3 className="text-sm font-semibold text-foreground">Recent activity <span className="font-normal text-muted-foreground">· last 6 months</span></h3>
          </div>
          <div className="divide-y divide-border">
            {recentItems.map((item, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-2 min-h-11">
                {item.type === 'invoice' ? (
                  <Receipt className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                ) : (
                  <FileText className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {item.type === 'invoice' ? (
                      <>
                        <span className="font-mono text-sm font-medium tabular-nums text-foreground">₹{Number(item.data.total_amount).toLocaleString('en-IN')}</span>
                        <StatusChip tone={
                          item.data.payment_status === 'paid' ? 'success' :
                          item.data.payment_status === 'partial' ? 'accent' :
                          item.data.payment_status === 'cancelled' ? 'neutral' : 'warning'
                        } className="capitalize">
                          {item.data.payment_status}
                        </StatusChip>
                      </>
                    ) : (
                      <span className="text-sm text-foreground truncate">
                        Prescription{item.data.diagnosis ? ` · ${item.data.diagnosis}` : ''}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground tabular-nums">{formatDate(item.date)}</p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {item.type === 'invoice' && (
                    <>
                      {can('billing:write') && (item.data.payment_status === 'unpaid' || item.data.payment_status === 'partial') && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2.5 text-xs"
                          onClick={() => onCollectPayment(item.data)}
                        >
                          Collect
                        </Button>
                      )}
                      <button
                        onClick={() => onPrintInvoice(item.data)}
                        className="h-7 w-7 flex items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                        title="Print invoice" aria-label="Print invoice"
                      >
                        <Printer className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  {item.type === 'rx' && can('prescriptions:read') && (
                    <button
                      onClick={() => onPrintPrescription(item.data)}
                      className="h-7 w-7 flex items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                      title="Print prescription" aria-label="Print prescription"
                    >
                      <Printer className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && recentItems.length === 0 && (
        <div className="text-center py-8">
          <p className="text-sm text-muted-foreground">No visits in the last 6 months.</p>
        </div>
      )}
    </div>
  );
}
